import cx from 'classnames'
import { Avatar } from '@/components/Avatar'
import { LoadingOverlay } from '@/components/LoadingOverlay'
import { Text } from '@/components/Text'
import { useElementSize } from '@/hooks/element-hooks'
import { useLoading } from '@/hooks/loading-hooks'
import type {
  SummaryDivider,
  SummaryItem,
} from '@/features/jobs/drafting/quote-display'
import type { LegendInfo } from '@/features/jobs/drafting/SubmissionViewModel'
import styles from './Summary.module.css'

export interface SummaryProps {
  legendInfo?: LegendInfo | null
  summaryItems?: (SummaryItem | SummaryDivider)[]
  isLoading: boolean
}

/**
 * Breakpoint for the element width.
 * If it goes below, use the vertical layout.
 */
const VERTICAL_LAYOUT_BREAKPOINT = 680

export function Summary({ legendInfo, summaryItems, isLoading }: SummaryProps) {
  const { size, connectRef } = useElementSize()
  const hasSummary = Boolean(summaryItems?.length)
  // Use vertical layout when space becomes limited
  // or if showing less than 3 items. The horizontal layout
  // with 2 or less items looks terrible.
  const useVerticalLayout =
    size.width <= VERTICAL_LAYOUT_BREAKPOINT || (summaryItems?.length ?? 0) < 3

  // Ensures we only show the loader if loading for more than a perceivable
  // duration, but only for subsequent loads.
  const showingLoader = useLoading(isLoading)
  return (
    <div
      ref={connectRef}
      className={cx(
        styles.root,
        useVerticalLayout && styles.vertical,
        hasSummary && styles.hasSummary,
      )}
    >
      {legendInfo && <SummaryLegendInfo legendInfo={legendInfo} />}
      {hasSummary && <Info items={summaryItems!} />}
      {showingLoader && <LoadingOverlay testId="summary-loading-overlay" />}
    </div>
  )
}

interface SummaryLegendInfoProps {
  legendInfo: LegendInfo
}

/**
 * Legend info.
 */
function SummaryLegendInfo({ legendInfo }: SummaryLegendInfoProps) {
  return (
    <div className={styles.legend}>
      <div className={styles.legendContent}>
        <div className={styles.legendVisual}>
          <Avatar
            size={64}
            subject={{
              displayName: legendInfo.title,
              avatar: legendInfo.imageUrl,
            }}
          />
        </div>
        <div className={styles.legendTitle}>{legendInfo.title}</div>
      </div>
    </div>
  )
}

interface InfoProps {
  items: (SummaryItem | SummaryDivider)[]
}

export function Info({ items }: InfoProps) {
  return (
    <div className={styles.info}>
      <div className={styles.infoContent}>
        {items.map((item, i) => {
          switch (item.type) {
            case 'Divider':
              return <div key={i} className={styles.line} />
            case 'Item':
              return (
                <Item
                  key={i}
                  highlighted={item.highlighted}
                  label={item.label}
                  value={item.value}
                  testId={`summary-item-${item.label}`}
                />
              )
          }

          return null
        })}
      </div>
    </div>
  )
}

interface ItemProps {
  label: string
  value: string
  highlighted?: boolean
  testId?: string
}

export function Item({ label, value, highlighted, testId }: ItemProps) {
  return (
    <div className={cx(styles.item, highlighted && styles.highlighted)}>
      <div className={styles.itemLabel} data-test={`${testId}-label`}>
        {label}
      </div>
      <div className={styles.itemValue} data-test={`${testId}-value`}>
        <Text text={value} />
      </div>
    </div>
  )
}
